import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Flex, useDisclosure } from '@chakra-ui/react';
import {
  ContentSkip,
  Navbar,
  NavbarActionButtons,
  SidebarCollapse,
  SpinnerModal,
  setUserAlias,
  removeUserAlias,
  getUserAlias,
  AgentTermsModal,
} from 'shared-components';
import { acceptTerms as authActions_acceptTerms, logout } from '@/store/auth/actions';
import { ApplicationState } from '@/store';
import { hasPerm, isPartnerRole, P } from '@/utilities/roles/permissions';
import { LeftBar } from './left-bar/index';
import { getRole } from '@/store/selectors';
import { useSession } from 'shared-components/authentication';
import { getAdmAllowedAliases } from '@/store/config/actions';
import { UserRole } from '@/store/auth/types';
import {
  useLoadActiveTab,
  useLoadAgents,
  useLoadFilters,
  useLoading,
  useLoadTabs,
  useLoadUniConfig,
  useUpdateSearchParams,
} from './hooks';
import { ApplyDataGrid } from '@/components/organisms/ApplyDataGrid';
import { defaultTab } from '@/store/offers-dashboard/types';
import { QueryKeys } from '@/types';

export const OffersDashboardPage = () => {
  const dispatch = useDispatch();
  const session = useSession();

  const acceptTerms = useCallback(() => dispatch(authActions_acceptTerms()), [dispatch]);
  const logOut = useCallback(() => dispatch(logout()), [dispatch]);
  const userRole = useSelector(getRole);

  const { isAcceptedTerms, groups, uni, isLoading, activeTab, allowedAliases, currentUser, userName } = useSelector(
    ({ auth, config, ui, offersDashboard }: ApplicationState) => {
      const isAdminOrEnroly = auth.role === UserRole.Admin || auth.role === UserRole.Enroly;
      return {
        isAcceptedTerms: auth.terms,
        groups: auth.groups,
        uni: isPartnerRole(userRole) ? auth?.currentUser?.partner : auth.uni,
        intakeId: auth.intake,
        isLoading: ui.isLoading,
        activeTab: offersDashboard.currentTab,
        allowedAliases: isAdminOrEnroly ? config.admAllowedAliases || [] : config.allowedAliases || [],
        currentUser: auth.currentUser,
        userName: auth.student_number,
      };
    },
  );

  useLoadActiveTab(defaultTab, QueryKeys.CURRENT_TAB);

  const canViewInsights = hasPerm(userRole, P.VIEW_INSIGHTS) || groups.includes('Insights');
  const canViewConfig = hasPerm(userRole, P.VIEW_CONFIG);

  const onLogout = async () => {
    logOut();
    await session.onLogout();
  };

  // Fetch ADM aliases for Admin/Enroly users
  const isAdminOrEnroly = userRole === UserRole.Admin || userRole === UserRole.Enroly;
  useEffect(() => {
    if (isAdminOrEnroly) {
      dispatch(getAdmAllowedAliases());
    }
  }, [isAdminOrEnroly, dispatch]);

  // Build Act As options
  const currentUserName = useMemo(() => {
    return currentUser?.givenName && currentUser?.familyName
      ? `${currentUser.givenName} ${currentUser.familyName}`
      : userName || '';
  }, [currentUser, userName]);

  const actAsOptions = useMemo(() => {
    return allowedAliases && allowedAliases.length > 0 ? [currentUserName, ...allowedAliases].filter(Boolean) : [];
  }, [allowedAliases, currentUserName]);

  const currentAlias = getUserAlias() || currentUserName;

  const handleActAsChange = useCallback(
    (selectedAlias: string) => {
      if (selectedAlias === currentUserName) {
        removeUserAlias();
      } else {
        setUserAlias(selectedAlias);
      }
      window.location.reload();
    },
    [currentUserName],
  );

  // Get sidebar collapsed state from localStorage
  const { isOpen, onToggle } = useDisclosure({
    defaultIsOpen: JSON.parse(localStorage.getItem('sidebarIsOpen') || 'true'), // Default to true if not set
  });

  // Update localStorage whenever isOpen changes
  useEffect(() => {
    localStorage.setItem('sidebarIsOpen', JSON.stringify(isOpen));
  }, [isOpen]);

  useLoading();
  useLoadUniConfig(uni);
  useLoadFilters(activeTab);
  useLoadTabs(activeTab);
  useUpdateSearchParams(activeTab);
  useLoadAgents();

  return (
    <Box
      bgColor="chakra-body-bg"
      position="absolute"
      left={{ base: 0, md: 'navbarWidth' }}
      width={{ base: '100%', md: 'calc(100vw - var(--ds-sizes-navbarWidth))' }}
      height={'100vh'}
    >
      <SpinnerModal show={isLoading} />

      <ContentSkip />

      <div id="content-loaded" data-testid="offers-dashboard" />

      <Navbar
        actionableButtonsStates={{
          [NavbarActionButtons.STUDENTS]: {
            isVisible: true,
            isSelected: true,
          },
          [NavbarActionButtons.INSIGHTS]: {
            isVisible: canViewInsights,
          },
          [NavbarActionButtons.SETTINGS]: {
            isVisible: canViewConfig,
          },
          [NavbarActionButtons.FEEDBACK]: {
            isVisible: true,
          },
        }}
        username={currentUserName}
        isAuthenticated={true}
        onLogout={onLogout}
        subnav={<LeftBar />}
        actAs={
          actAsOptions.length > 1
            ? {
                options: actAsOptions,
                currentValue: currentAlias,
                onChange: handleActAsChange,
              }
            : undefined
        }
      />

      <Flex overflowX="auto" overflowY="hidden" width={'100%'} height={'100%'} data-selector="content-wrap">
        <SidebarCollapse
          label="Stages navigation"
          isOpen={isOpen}
          onToggle={onToggle}
          containerProps={{ height: '100%' }}
        >
          <LeftBar />
        </SidebarCollapse>

        <Flex as="main" id="main" height="100%" overflow="hidden" flexDirection="column" flexGrow={1}>
          <ApplyDataGrid />
        </Flex>
      </Flex>

      {!isAcceptedTerms && !isLoading && <AgentTermsModal onAcceptTerms={acceptTerms} />}
    </Box>
  );
};
